import React, { useState } from 'react';
import { styled } from '@abyss/web/tools/styled';
import { Accordion } from '@abyss/web/ui/Accordion';
import { Box } from '@abyss/web/ui/Box';
import { Flex } from '@abyss/web/ui/Flex';
import { Heading } from '@abyss/web/ui/Heading';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { Text } from '@abyss/web/ui/Text';
import { useRouter } from 'next/compat/router';
import { ContentWidthWrapper } from '@pass-frontend/abyss/common/components/content-width-wrapper/ui';
import { MEDIA_QUERY } from '@pass-frontend/abyss/utility/style-helpers';
import {
  EventType,
  PageTags,
  getStandAlonePageName,
  logAnalyticsEvent,
  captureEventTrigger,
} from '@pass-frontend/common/analytics';
import { STANDALONE_PATHS } from '@pass-frontend/common/constants/paths';
import { useIntl } from '@pass-frontend/common/i18n';

const CSS_BOX = {
  padding: '32px 0',
  margin: '0 auto',

  [MEDIA_QUERY.desktop]: {
    padding: '64px 0',
  },
};

const ACCORDION_WRAPPER_CSS = {
  maxWidth: '840px',
  margin: '0 auto',
};

const getAccordionTriggerCss = (bgColor: string) => ({
  'abyss-accordion-trigger': {
    alignItems: 'center',
    backgroundColor: `${bgColor} !important`,
    borderBottom: 'none',
    minHeight: 0,
    padding: '$lg $md',
  },
  'abyss-chevron-icon': { fill: '$primary1', marginLeft: '$sm' },
});

const getAccordionContentCss = (bgColor: string) => ({
  'abyss-accordion-content-text': {
    backgroundColor: bgColor,
    padding: '$xs $md $sm',
  },
});

const StyledAccordionItem = styled(Accordion.Item, {
  '&.abyss-accordion-item': {
    borderBottom: '1px solid $gray5',
    borderRadius: 0,
  },
});

const StyledLink = styled('a', {
  fontWeight: '$bold',
});

interface Item {
  question?: string;
  isActive: boolean;
}

interface QuesAns {
  question: string;
  answer: string;
}

export function FaqSection(props: {
  quesAns: QuesAns[];
  seeMore?: boolean;
  bgColor?: string;
}) {
  const { t } = useIntl();
  const { quesAns, seeMore = true, bgColor = '$gray1' } = props;
  const ACCORDION_TRIGGER_CSS = getAccordionTriggerCss(bgColor);
  const ACCORDION_CONTENT_CSS = getAccordionContentCss(bgColor);
  const router = useRouter();

  const [openAccordionList, setOpenAccordionList] = useState<string[]>([]);

  const routeToHelpAndSupportCenter = (
    event: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    captureEventTrigger(event.nativeEvent);
    router?.push(STANDALONE_PATHS.FAQS);
  };

  const getClickedItem = (updatedOpenAccordionList: string[]): Item => {
    const difference = [
      ...openAccordionList.filter((v) => !updatedOpenAccordionList.includes(v)),
      ...updatedOpenAccordionList.filter((v) => !openAccordionList.includes(v)),
    ];
    return {
      question: difference[0],
      isActive: openAccordionList.length < updatedOpenAccordionList.length,
    };
  };

  const onValueChange = (updatedOpenAccordionList: string[]) => {
    const { question, isActive } = getClickedItem(updatedOpenAccordionList);
    setOpenAccordionList(updatedOpenAccordionList);

    logAnalyticsEvent(EventType.CustomUserEvent, {
      pageName: getStandAlonePageName(router?.pathname ?? ''),
      type: isActive ? 'Accordion open' : 'Accordion closed',
      [PageTags.IsFilter]: false,
      [PageTags.FilterName]: '',
      [PageTags.FilterValues]: '',
      [PageTags.IsSelectedTier]: false,
      [PageTags.NewPageLoad]: false,
      [PageTags.SelectedTiers]: '',
      [PageTags.AvailableTiers]: '',
      [PageTags.TriggerUIElementName]: question,
      [PageTags.TriggerUISection]: ['Faq Section'],
    });
  };

  return (
    <Box
      color={bgColor}
      css={CSS_BOX}
      className="faq-section__content-width-wrapper"
    >
      <ContentWidthWrapper>
        <Heading offset={2} textAlign="center" css={{ marginBottom: '32px' }}>
          {t('faq.title')}
        </Heading>
        <Flex css={ACCORDION_WRAPPER_CSS} justify="left">
          <Accordion type="multiple" onValueChange={onValueChange}>
            {quesAns.map(({ question, answer }: QuesAns, index: number) => (
              <StyledAccordionItem key={index} value={question}>
                <Accordion.Trigger css={ACCORDION_TRIGGER_CSS}>
                  <Heading offset={5}>{question}</Heading>
                </Accordion.Trigger>
                <Accordion.Content css={ACCORDION_CONTENT_CSS}>
                  <Text color="$gray7">{answer}</Text>
                </Accordion.Content>
              </StyledAccordionItem>
            ))}
          </Accordion>
        </Flex>
        {seeMore && (
          <Flex justify="center" css={{ marginTop: '32px' }}>
            <Flex justify="center">
              <StyledLink
                data-ui-element-name="unauthenticated-faq-link"
                onClick={routeToHelpAndSupportCenter}
              >
                {t('faq.seeMoreLink')}
              </StyledLink>
              <IconMaterial icon="chevron_right" />
            </Flex>
          </Flex>
        )}
      </ContentWidthWrapper>
    </Box>
  );
}
